import React, { useMemo, useState } from 'react';
import { Grid } from '@mui/material';
import { api } from 'common';
import DirectionsCarOutlinedIcon from '@mui/icons-material/DirectionsCarOutlined';
import RouteOutlinedIcon from '@mui/icons-material/RouteOutlined';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import { C2Select, C2SummaryCard } from '../components/Table/C2UserTable';
import C2ReportTable, { C2Cell, formatCop, monthOptions } from '../components/reports/C2ReportTable';

export default function DriverEarning({ bookings, loading }) {
  const rows = useMemo(() => api.buildDriverEarnings(bookings || []), [bookings]);
  const months = useMemo(() => monthOptions(rows, { allLabel: 'Todos los meses' }), [rows]);
  const [pickedMonth, setMonth] = useState(null);
  const month = pickedMonth ?? (months[1] ? months[1].value : '');

  const filtered = useMemo(
    () => rows.filter((r) => !month || r.monthKey === month).map((r) => ({ ...r, monthLabel: api.reportMonthLabel(r.monthKey) })),
    [rows, month]
  );

  const summary = useMemo(
    () => ({
      drivers: new Set(filtered.map((r) => r.driverUid)).size,
      rides: filtered.reduce((acc, r) => acc + r.rides, 0),
      earnings: filtered.reduce((acc, r) => acc + r.earnings, 0),
    }),
    [filtered]
  );

  const columns = [
    { title: 'Mes', field: 'monthLabel', customSort: (a, b) => a.monthKey.localeCompare(b.monthKey) },
    { title: 'Conductor', field: 'driverName', render: (r) => <C2Cell primary={r.driverName} secondary={r.vehicleNo ? `Placa ${r.vehicleNo}` : null} /> },
    { title: 'Placa', field: 'vehicleNo', hidden: true, export: true },
    { title: 'Viajes', field: 'rides', type: 'numeric' },
    { title: 'Ganancia del conductor', field: 'earnings', type: 'numeric', render: (r) => <C2Cell primary={formatCop(r.earnings)} color="#15803D" /> },
  ];

  const periodCaption = month ? api.reportMonthLabel(month) : 'Todos los meses';

  return (
    <>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={DirectionsCarOutlinedIcon} label="Conductores con viajes" value={summary.drivers} caption={periodCaption} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={RouteOutlinedIcon} label="Viajes completados" value={summary.rides} caption={periodCaption} />
        </Grid>
        <Grid item xs={12} sm={4}>
          <C2SummaryCard icon={PaymentsOutlinedIcon} label="Ganancia de conductores" value={formatCop(summary.earnings)} caption={periodCaption} accent="#16A34A" />
        </Grid>
      </Grid>
      <C2ReportTable
        title="Ganancias de conductores"
        subtitle="Lo que ganó cada conductor por mes en viajes completados (parte del conductor)."
        columns={columns}
        data={filtered}
        loading={loading}
        emptyIcon={DirectionsCarOutlinedIcon}
        emptyTitle="Sin viajes en este periodo"
        emptyMessage="Cuando los conductores completen viajes, sus ganancias aparecerán aquí."
        csvFileName="ganancias_conductores.csv"
        toolbarExtras={[
          <C2Select key="m" id="driver-earning-month" label="Mes" value={month} onChange={(e) => setMonth(e.target.value)} options={months} minWidth={190} />,
        ]}
      />
    </>
  );
}
